import React, { useState } from "react";
import JSONPretty from "react-json-pretty";
import "react-json-pretty/themes/monikai.css";
import { FaCopy } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RecipeSchemaForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    keywords: "",
    image: "",
    videoContentUrl: "",
    videoEmbedUrl: "",
    author: "",
    datePublished: "",
    category: "",
    cuisine: "",
    prepTime: "",
    cookTime: "",
    totalTime: "",
    servingSize: "",
    calories: "",
    fat: "",
    ingredients: [],
    steps: [],
    reviews: [],
    aggregateRatingValue: "",
    numberOfRatings: "",
    highestAllowed: "",
    lowestAllowed: "",
  });

  const [generatedSchema, setGeneratedSchema] = useState({
    "@context": "https://schema.org",
    "@type": "Recipe",
    "name": "",
    "image": "",
    "description": "",
    "keywords": "",
    "author": {
      "@type": "Person",
      "name": ""
    },
    "datePublished": "",
    "prepTime": "",
    "cookTime": "",
    "totalTime": "",
    "recipeCategory": "",
    "recipeCuisine": "",
    "recipeIngredient": [],
    "recipeInstructions": [],
    "nutrition": {
      "@type": "NutritionInformation",
      "servingSize": "",
      "calories": "",
      "fatContent": ""
    },
    "review": []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, ""],
    });
  };

  const handleIngredientChange = (index, value) => {
    const updatedIngredients = formData.ingredients.map((ingredient, i) =>
      i === index ? value : ingredient
    );
    setFormData({ ...formData, ingredients: updatedIngredients });
  };

  const removeIngredient = (index) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index),
    });
  };

  const addStep = () => {
    setFormData({
      ...formData,
      steps: [...formData.steps, ""],
    });
  };

  const handleStepChange = (index, value) => {
    const updatedSteps = formData.steps.map((step, i) =>
      i === index ? value : step
    );
    setFormData({ ...formData, steps: updatedSteps });
  };

  const removeStep = (index) => {
    setFormData({
      ...formData,
      steps: formData.steps.filter((_, i) => i !== index),
    });
  };

  const addReview = () => {
    setFormData({
      ...formData,
      reviews: [...formData.reviews, { author: "", reviewBody: "", ratingValue: "" }],
    });
  };

  const handleReviewChange = (index, field, value) => {
    const updatedReviews = formData.reviews.map((review, i) =>
      i === index ? { ...review, [field]: value } : review
    );
    setFormData({ ...formData, reviews: updatedReviews });
  };

  const removeReview = (index) => {
    setFormData({
      ...formData,
      reviews: formData.reviews.filter((_, i) => i !== index),
    });
  };

  const generateSchema = () => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Recipe",
      name: formData.name,
      image: formData.image,
      description: formData.description,
      keywords: formData.keywords,
      author: {
        "@type": "Person",
        name: formData.author,
      },
      datePublished: formData.datePublished,
      prepTime: formData.prepTime,
      cookTime: formData.cookTime,
      totalTime: formData.totalTime,
      recipeCategory: formData.category,
      recipeCuisine: formData.cuisine,
      recipeIngredient: formData.ingredients,
      recipeInstructions: formData.steps.map((step) => ({
        "@type": "HowToStep",
        text: step,
      })),
      nutrition: {
        "@type": "NutritionInformation",
        servingSize: formData.servingSize,
        calories: formData.calories,
        fatContent: formData.fat,
      },
      aggregateRating: formData.aggregateRatingValue
        ? {
            "@type": "AggregateRating",
            ratingValue: formData.aggregateRatingValue,
            reviewCount: formData.numberOfRatings,
            bestRating: formData.highestAllowed,
            worstRating: formData.lowestAllowed,
          }
        : undefined,
      review: formData.reviews.map((review) => ({
        "@type": "Review",
        author: review.author,
        reviewBody: review.reviewBody,
        reviewRating: {
          "@type": "Rating",
          ratingValue: review.ratingValue,
        },
      })),
    };
    setGeneratedSchema(JSON.stringify(schema, null, 2));
    toast.success("Schema generated successfully!");
  };

  const handleCopySchema = () => {
    if (generatedSchema) {
      navigator.clipboard.writeText(generatedSchema);
      toast.info("Schema copied to clipboard!");
    } else {
      toast.error("No schema to copy!");
    }
  };

  const deleteSchema = () => {
    setGeneratedSchema({
      "@context": "https://schema.org",
      "@type": "Recipe",
      "name": "",
      "image": "",
      "description": "",
      "keywords": "",
      "author": {
        "@type": "Person",
        "name": ""
      },
      "datePublished": "",
      "prepTime": "",
      "cookTime": "",
      "totalTime": "",
      "recipeCategory": "",
      "recipeCuisine": "",
      "recipeIngredient": [],
      "recipeInstructions": [],
      "nutrition": {
        "@type": "NutritionInformation",
        "servingSize": "",
        "calories": "",
        "fatContent": ""
      },
      "review": []
    });
    toast.warning("Schema reset to dummy data!");
  };
  

  const handleSubmit = (e) => {
    e.preventDefault();
    generateSchema();
  };

  return (
    <div className="container mt-4">
      <ToastContainer />
      <h3 className="mb-4">Recipe Schema Generator</h3>
      <div className="row">
        <div className="col-md-6">
          <form onSubmit={handleSubmit} className="card p-4 shadow-sm">
            <div className="row mb-3">
              <div className="col-md-12">
                <label className="form-label">Recipe Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter recipe name"
                />
              </div>
              <div className="col-md-12">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter recipe description"
                  rows="3"
                ></textarea>
              </div>
              <div className="col-md-12">
                <label className="form-label">Keywords</label>
                <textarea
                  className="form-control"
                  name="description"
                  value={formData.keywords}
                  onChange={handleInputChange}
                  placeholder="Enter recipe keywords"
                  rows="3"
                ></textarea>
              </div>
              <div className="col-md-6">
                <label className="form-label">Image</label>
                <input
                  type="text"
                  className="form-control"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  placeholder="Enter image URL"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Video Content Url</label>
                <input
                  type="text"
                  className="form-control"
                  name="videoContentUrl"
                  value={formData.videoContentUrl}
                  onChange={handleInputChange}
                  placeholder="Enter videoContent URL"
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Video Embed Url</label>
                <input
                  type="text"
                  className="form-control"
                  name="videoEmbedUrl"
                  value={formData.videoEmbedUrl}
                  onChange={handleInputChange}
                  placeholder="Enter videoEmbed URL"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Author</label>
                <input
                  type="text"
                  className="form-control"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="Enter author"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Date OF  Published</label>
                <input
                  type="date"
                  className="form-control"
                  name="datePublished"
                  value={formData.datePublished}
                  onChange={handleInputChange}
                 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Category</label>
                <input
                  type="text"
                  className="form-control"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="Enter Category"
                 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Cuisine</label>
                <input
                  type="text"
                  className="form-control"
                  name="cuisine"
                  value={formData.cuisine}
                  onChange={handleInputChange}
                   placeholder="Enter Cuisine"
                 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Prep Time</label>
                <input
                  type="text"
                  className="form-control"
                  name="prepTime"
                  value={formData.prepTime}
                  onChange={handleInputChange}
                 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Cook Time</label>
                <input
                  type="text"
                  className="form-control"
                  name="cookTime"
                  value={formData.cookTime}
                  onChange={handleInputChange}
                 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Total Time</label>
                <input
                  type="text"
                  className="form-control"
                  name="totalTime"
                  value={formData.totalTime}
                  onChange={handleInputChange}
                 
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Serving Size</label>
                <input
                  type="text"
                  className="form-control"
                  name="servingSize"
                  value={formData.servingSize}
                  onChange={handleInputChange}
                 placeholder="Enter Serving Size"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Calories</label>
                <input
                  type="number"
                  className="form-control"
                  name="calories"
                  value={formData.calories}
                  onChange={handleInputChange}
                 placeholder="Enter Calories"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Fat</label>
                <input
                  type="number"
                  className="form-control"
                  name="fat"
                  value={formData.fat}
                  onChange={handleInputChange}
                 placeholder="Enter Fat"
                />
              </div>
            </div>

            <h5>Ingredients</h5>
            {formData.ingredients.map((ingredient, index) => (
              <div key={index} className="row align-items-end mb-2">
                <div className="col-md-10">
                  <input
                    type="text"
                    className="form-control"
                    value={ingredient}
                    onChange={(e) => handleIngredientChange(index, e.target.value)}
                    placeholder="Enter ingredient"
                  />
                </div>
                <div className="col-md-2 text-end">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => removeIngredient(index)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-primary mb-3" onClick={addIngredient}>
              + Add Ingredient
            </button>
            <h5>Steps</h5>
            {formData.steps.map((step, index) => (
              <div key={index} className="row align-items-end mb-2">
                <div className="col-md-10">
                  <input
                    type="text"
                    className="form-control"
                    value={step}
                    onChange={(e) => handleStepChange(index, e.target.value)}
                    placeholder="Enter ingredient"
                  />
                </div>
                <div className="col-md-2 text-end">
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => removeStep(index)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-primary mb-3" onClick={addStep}>
              + Add Steps
            </button>
            <h5>Reviews</h5>
{formData.reviews.map((review, index) => (
  <div key={index} className="row align-items-end mb-2">
    <div className="col-md-6">
      <label className="form-label">Author</label>
      <input
        type="text"
        className="form-control"
        value={review.author}
        onChange={(e) => handleReviewChange(index, "author", e.target.value)}
        placeholder="Enter review author"
      />
    </div>
    <div className="col-md-6">
      <label className="form-label">Rating</label>
      <input
        type="number"
        className="form-control"
        value={review.ratingValue}
        onChange={(e) => handleReviewChange(index, "ratingValue", e.target.value)}
        placeholder="Rating (1-5)"
        min="1"
        max="5"
      />
    </div>
    <div className="col-md-12">
      <label className="form-label">Review Body</label>
      <input
        type="text"
        className="form-control"
        value={review.reviewBody}
        onChange={(e) => handleReviewChange(index, "reviewBody", e.target.value)}
        placeholder="Enter review content"
      />
    </div>
    
    <div className="col-md-2 text-end">
      <button
        type="button"
        className="btn btn-danger mt-4"
        onClick={() => removeReview(index)}
      >
        Delete
      </button>
    </div>
  </div>
))}
<button type="button" className="btn btn-primary mb-3" onClick={addReview}>
  + Add Review
</button>


            <button type="submit" className="btn btn-success w-100">
              Generate Schema
            </button>
          </form>
        </div>

        <div className="col-md-6">
          <div className="card shadow-sm p-4">
            <h5 className="card-title text-center mb-4">Generated JSON-LD</h5>
            <div className="d-flex justify-content-center align-items-center mb-3 mt-3">
  <button className="btn btn-primary me-2" onClick={handleCopySchema}>
    Copy
  </button>
  <button className="btn btn-danger me-2" onClick={deleteSchema}>
    Reset
  </button>
  <button className="btn btn-info me-2">
    <a
      target="_blank"
      rel="noopener noreferrer"
      className="text-decoration-none text-white"
      href="https://developers.google.com/search/docs/appearance/structured-data"
    >
      Validate
    </a>
  </button>
  <button className="btn btn-success">
    <a
      target="_blank"
      rel="noopener noreferrer"
      className="text-decoration-none text-white"
      href="https://search.google.com/test/rich-results"
    >
      Test
    </a>
  </button>
</div>
            {generatedSchema ? (
              <div className="position-relative">
                <JSONPretty
                  id="json-pretty"
                  data={generatedSchema}
                  className="bg-light p-3 rounded border"
                ></JSONPretty>
                
              </div>
            ) : (
              <p className="text-center">No schema generated yet.</p>
            )}
           
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeSchemaForm;
